import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

/**
 * Audience-aware banner shown when the active org's plan has expired.
 *
 * Audience-specific copy + CTA:
 *   - 'client' / 'solo-trainer' → "Your plan has expired" + Upgrade button
 *   - 'trainer' (org member)    → "Your organization's plan has expired" (no button — they can't pay)
 *
 * Props:
 *   audience: 'client' | 'solo-trainer' | 'trainer'   (default: 'client')
 *   planEnd:  ISO date string                          (optional — included in copy if present)
 *   upgradePath: string                                (defaults are role-aware; override for routing tweaks)
 */
const PlanExpiredBanner = ({ audience = 'client', planEnd, upgradePath }) => {
  const navigate = useNavigate();

  const showCTA = audience === 'client' || audience === 'solo-trainer';
  const defaultPath = audience === 'solo-trainer' ? '/trainer/billing' : '/client/subscription';
  const path = upgradePath || defaultPath;

  const headline =
    audience === 'trainer'
      ? "Your organization's plan has expired"
      : 'Your plan has expired';
  // Use "upgrade" not "renew" — the Free plan isn't renewable (1 month, then
  // must upgrade to a paid plan), and an unrenewed paid plan also gets
  // downgraded to free+expired by the cron, so the recovery path is the
  // same in both cases: pick a paid plan and go through Dodo checkout.
  const body =
    audience === 'trainer'
      ? 'Creating new sessions and launching activities is paused. Please contact your admin to upgrade.'
      : 'Creating new sessions, launching activities, and managing trainers is paused until you upgrade to continue.';

  const formattedEnd = planEnd
    ? new Date(planEnd).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  return (
    <div className="mb-6 p-4 sm:p-5 bg-red-50 border border-red-200 rounded-xl flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="flex items-start gap-3 flex-1">
        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-5 h-5 text-red-600" />
        </div>
        <div>
          <h3 className="text-sm sm:text-base font-semibold text-red-900">{headline}</h3>
          <p className="text-xs sm:text-sm text-red-700 mt-0.5">
            {body}
            {formattedEnd && <span className="ml-1 text-red-600">(ended {formattedEnd})</span>}
          </p>
        </div>
      </div>
      {showCTA && (
        <button
          onClick={() => navigate(path)}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition-colors flex-shrink-0"
        >
          Upgrade Plan
        </button>
      )}
    </div>
  );
};

export default PlanExpiredBanner;
